import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import DefaultLayout from "./layouts/DefaultLayout";
import { Home } from "./components/pages/home";
import OsPoi from "./components/dataSelection/OsPoi/OsPoi";
import OsmPoi from "./components/dataSelection/OsmPoi/OsmPoi";
import PublicTransport from "./components/dataSelection/PublicTransport";
import RouteLines from "./components/dataSelection/RouteLines";
import Highways from "./components/dataSelection/Highways";
import Naptan from "./components/dataSelection/Naptan";
import MRN from "./components/dataSelection/MRN";
import OpenRoads from "./components/dataSelection/OpenRoads";
import OsmRoads from "./components/dataSelection/OSMRoads";
import Census from "./components/dataSelection/Census/Census";
import CodePoint from "./components/dataSelection/OSCodePoints";
import UsCensus from "./components/dataSelection/UsCensus/UsCensus";
import { Admin } from "./components/pages/admin";
import { RequestHistory } from "./components/admin/RequestHistory/RequestHistory";
import RequestStatistics from "./components/admin/RequestStatistics/RequestStatistics";
import Queue from "./components/admin/Queue/Queue";
import ProcessingTimes from "./components/admin/ProcessingTimes/ProcessingTimes";
import PopulationCentroids from "./components/dataSelection/PopulationCentroids";
import { InrixTrip } from "./components/dataSelection/InrixTrip/InrixTrip";
import { InrixVolumes } from "./components/dataSelection/InrixVolumes/InrixVolumes";
import { InrixSpeedProfiles } from "./components/dataSelection/InrixSpeedProfiles/InrixSpeedProfiles";
import Users from "./components/admin/Users/Users";
import LicenceTypes from "./components/admin/LicenceTypes/LicenceTypes";
import Royalties from "./components/admin/Royalties/Royalties";
import Orders from "./components/admin/Orders/Orders";
import LayerRoutes from "./components/admin/Layers/LayerRoutes";
import LayerOverview from "./components/admin/Layers/LayerOverview";
import ImportLogs from "./components/admin/Layers/ImportLogs/ImportLogs";
import { DownloadInfo } from "./components/admin/PublicTransport/DownloadInfo";
import FeedsView from "./components/admin/Layers/PublicTransport/FeedsView";
import PtImportsView from "./components/admin/Layers/PublicTransport/PtImportsView";
import DeleteQuarter from "./components/admin/Layers/RouteLine/DeleteQuarter";
import Notifications from "./components/admin/Notification/Notifications";
import { DownloadData } from "./components/pages/DownloadData";
import Basket from "./components/pages/basket";
import UserOrders from "./components/pages/userOrders";
import UserOrderPage from "./components/pages/UserOrderPage";
import Banner from "./components/admin/Banner/Banner";
import BoundaryList from "./components/pages/seo/BoundaryList";
import DataSets from "./components/pages/seo/Datasets/DataSets";
import Redirecter from "./components/pages/seo/Redirecter";
import LoginLayout from "./layouts/LoginLayout";
import { ForgottenPassword } from "./components/pages/forgottenPassword/ForgottenPassword";
import Register from "./components/pages/Register/Register";
import { Login } from "./components/pages/login/Login";
import PurchaseLicence from "./components/pages/PurchaseLicence/PurchaseLicence";
import MaintenancePage from "./components/pages/Maintenance";
import RenewalLandingPage from "./components/pages/RenewalLandingPage/RenewalLandingPage";
import { useEffect } from "react";
import { Roles } from "./constant/Roles";
import { useAuthContext } from "./components/auth/AuthContext";
import { useMaintenanceModeContext } from "./context/MaintenanceMode/MaintenanceModeContext";

const AppRoutes = () => {
  const { isMaintenance } = useMaintenanceModeContext();
  const authContext = useAuthContext();
  const isAdmin =
    authContext?.user?.roles.includes(Roles.ADMINISTRATOR) ?? false;
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = window.location.pathname;
    const isAdminLoginRoute = pathname === "/admin/login";

    if (isMaintenance == null) {
      return;
    }

    if (
      isMaintenance &&
      !isAdmin &&
      pathname !== "/maintenance" &&
      !isAdminLoginRoute
    ) {
      window.location.href = "/maintenance";
    } else if (!isMaintenance && window.location.pathname === "/maintenance") {
      window.location.href = "/";
    } else if (isAdmin && pathname === "/maintenance") {
      window.location.href = "/admin/login";
    }
  }, [isMaintenance, isAdmin, navigate]);

  return (
    <Routes>
      <Route path="/" element={<DefaultLayout />}>
        <Route path="/" element={<Home />}>
          <Route path="/os-poi/:boundary?" element={<OsPoi />} />
          <Route path="/osm-poi/:boundary?" element={<OsmPoi />} />
          <Route
            path="/public-transport/:boundary?"
            element={<PublicTransport />}
          />
          <Route path="/uk-route-lines/:boundary?" element={<RouteLines />} />
          <Route path="/naptan/:boundary?" element={<Naptan />} />
          <Route path="/os-highways/:mode?/:boundary?" element={<Highways />} />
          <Route path="/os-mrn/:boundary?" element={<MRN />} />
          <Route path="/os-openroads/:boundary?" element={<OpenRoads />} />
          <Route path="/osm-roads/:boundary?" element={<OsmRoads />} />
          <Route path="/uk-census/:boundary?" element={<Census />} />
          <Route path="/os-codepoints/:boundary?" element={<CodePoint />} />
          <Route path="/us-census/:boundary?" element={<UsCensus />} />
          <Route
            path="/population-centroids/:boundary?"
            element={<PopulationCentroids />}
          />
          <Route path="/inrix-trip/:boundary?" element={<InrixTrip />} />
          <Route path="/inrix-volumes/:boundary?" element={<InrixVolumes />} />
          <Route path="/inrix-speed-profiles/:boundary?" element={<InrixSpeedProfiles />} />
        </Route>
        <Route path="/view/:layer" element={<Home />} />

        <Route path="/admin" element={<Admin />}>
          <Route
            path="/admin/request-history"
            index
            element={<RequestHistory />}
          />
          <Route
            path="/admin/request-statistics"
            element={<RequestStatistics />}
          />
          <Route path="/admin/queue" element={<Queue />} />

          <Route path="/admin/processingtimes" element={<ProcessingTimes />} />

          <Route path="/admin/users" element={<Users />} />

          <Route path="/admin/licences" element={<LicenceTypes />} />

          <Route path="/admin/royalties" element={<Royalties />} />
          <Route path="/admin/orders" element={<Orders />} />

          <Route path="/admin/layers" element={<LayerOverview />} />
          <Route path="/admin/layers/:layerName" element={<LayerRoutes />} />
          <Route
            path="/admin/layers/:layerName/:layerTab"
            element={<LayerRoutes />}
          />
          <Route
            path="/admin/layers/:layerName/logs"
            element={<ImportLogs />}
          />
          <Route
            path="/admin/layers/:layerName/downloads/:downloadId"
            element={<DownloadInfo />}
          />
          <Route
            path="/admin/layers/:layerName/snapshots/:feedId"
            element={<FeedsView />}
          />
          <Route
            path="/admin/layers/:layerName/snapshots/:quarter/imports"
            element={<PtImportsView />}
          />
          <Route
            path="/admin/layers/:layerName/delete"
            element={<DeleteQuarter />}
          />
          <Route path="/admin/banner" element={<Banner />} />
          <Route path="/admin/notifications" element={<Notifications />} />
          <Route path="/admin/user-order-data" element={<UserOrders />} />
        </Route>

        <Route
          path="/download/data/:orderId/:orderItemId"
          element={<DownloadData />}
        />

        <Route path="/basket" element={<Basket />} />

        <Route path="/my-orders" element={<UserOrderPage />} />

        <Route path="/boundaries" element={<BoundaryList />} />
        <Route path="/boundaries/:boundary" element={<DataSets />} />
        <Route path="/boundaries/:boundary/:product" element={<Redirecter />} />
        <Route path="/datasets" element={<DataSets />} />
        <Route path="/datasets/:product" element={<BoundaryList />} />
        <Route path="/datasets/:product/:boundary" element={<Redirecter />} />
      </Route>

      <Route element={<LoginLayout />}>
        <Route path="/forgottenPassword" element={<ForgottenPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin/login" element={<Login />} />
      </Route>
      <Route path="/purchase-licence" element={<PurchaseLicence />} />
      <Route path="/renewal/:orderId/:orderItemId/:renewalEmailId/:action" element={<RenewalLandingPage />} />
      <Route path="/view/{layer}" />

      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default AppRoutes;
